import { z } from 'zod';

import { toDateString } from '../../utils/dateHelper';

import type {
  InvestmentComplianceLimitDataLevel,
  LimitSetupDraft,
} from './createInvestmentLimitModel';
import { isSelectedKpiAvailable } from './investmentLimitKpiAvailability';

export const limitModeSchema = z.enum([
  'Position',
  'Aggregation',
  'Exclusion',
  'Inclusion',
]);
export type LimitMode = z.infer<typeof limitModeSchema>;

export const filterOnlyLimitModeSchema = limitModeSchema.extract([
  'Inclusion',
  'Exclusion',
]);

export const limitTypeSchema = z.enum([
  'Regulatory',
  'Contractual',
  'Client Specific',
  'Internal',
]);
export type LimitType = z.infer<typeof limitTypeSchema>;

export const valueTypeSchema = z.enum(['absolute', 'relative']);
export type ValueType = z.infer<typeof valueTypeSchema>;

export const operatorSchema = z.enum(['eq', 'ne', 'gt', 'ge', 'lt', 'le']);
export type OperatorValue = z.infer<typeof operatorSchema>;

export const aggregationMethodSchema = z.enum(['Sum', 'Top N', 'Bottom N']);
export type AggregationMethod = z.infer<typeof aggregationMethodSchema>;

export const wizardStepSchema = z.enum([
  'overview',
  'parameter',
  'denominator',
  'workflow',
  'review',
]);
export type WizardStepKey = z.infer<typeof wizardStepSchema>;

export const portfolioFilterConditionSchema = z.union([
  z.object({
    field: z.string(),
    op: operatorSchema.extract(['eq', 'ne']),
    value: z.string(),
  }),
  z.object({
    field: z.string(),
    op: z.enum(['in', 'notin']),
    value: z.array(z.string()),
  }),
]);
export type PortfolioFilterCondition = z.infer<
  typeof portfolioFilterConditionSchema
>;

export const finiteNumberInputSchema = z
  .string()
  .trim()
  .min(1)
  .transform(Number)
  .pipe(z.number().finite());

export const positiveIntegerInputSchema = finiteNumberInputSchema.pipe(
  z.number().int().positive()
);

const nonEmptyStringSchema = z.string().trim().min(1);

const limitComparisonSchema = z.object({
  dataField: nonEmptyStringSchema,
  limitValue: finiteNumberInputSchema,
  ruleOperator: operatorSchema,
});

const aggregationSchema = z
  .object({
    aggregationMethod: aggregationMethodSchema,
    aggregationNumber: z.string(),
  })
  .and(limitComparisonSchema)
  .superRefine((value, context) => {
    if (
      value.aggregationMethod !== 'Sum' &&
      !positiveIntegerInputSchema.safeParse(value.aggregationNumber).success
    ) {
      context.addIssue({
        code: 'custom',
        message: 'A positive integer is required',
        path: ['aggregationNumber'],
      });
    }
  });

const positionSchema = z
  .object({
    evalMetric: nonEmptyStringSchema,
    evalOperator: operatorSchema,
    evalValue: finiteNumberInputSchema,
  })
  .and(limitComparisonSchema);

const overviewSchema = z.object({
  limitName: nonEmptyStringSchema,
  limitType: limitTypeSchema,
});

export const validitySchema = z
  .object({
    validFrom: z.date(),
    validIndefinitely: z.boolean(),
    validTo: z.date().nullable(),
  })
  .superRefine((validity, context) => {
    if (toDateString(validity.validFrom) < toDateString(new Date())) {
      context.addIssue({
        code: 'custom',
        message: 'The start date must not precede the current day',
        path: ['validFrom'],
      });
    }

    if (validity.validIndefinitely) {
      return;
    }
    if (validity.validTo == null) {
      context.addIssue({
        code: 'custom',
        message: 'An end date is required',
        path: ['validTo'],
      });
      return;
    }
    if (toDateString(validity.validTo) < toDateString(validity.validFrom)) {
      context.addIssue({
        code: 'custom',
        message: 'The end date must not precede the start date',
        path: ['validTo'],
      });
    }
  });

export function createDataSourceSchema(
  dataLevels: readonly InvestmentComplianceLimitDataLevel[]
) {
  return z
    .object({
      currencyType: nonEmptyStringSchema,
      dataLevelId: z
        .string()
        .min(1)
        .refine((id) => dataLevels.some((level) => level.id === id)),
      enabledFilterKeys: z.array(z.string()),
      filterValues: z.record(z.string(), z.array(z.string())),
    })
    .superRefine((source, context) => {
      for (const [index, key] of source.enabledFilterKeys.entries()) {
        if ((source.filterValues[key]?.length ?? 0) === 0) {
          context.addIssue({
            code: 'custom',
            message: 'An enabled filter requires at least one value',
            path: ['enabledFilterKeys', index],
          });
        }
      }
    });
}

export function getEvaluationSchema(limitMode: LimitMode) {
  if (limitMode === 'Position') {
    return positionSchema;
  }
  if (limitMode === 'Aggregation') {
    return aggregationSchema;
  }
  return z.unknown();
}

export function parseFiniteNumberInput(value: string) {
  const result = finiteNumberInputSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function parsePositiveIntegerInput(value: string) {
  const result = positiveIntegerInputSchema.safeParse(value);
  return result.success ? result.data : null;
}

function isEvaluationValid(draft: LimitSetupDraft) {
  return (
    getEvaluationSchema(draft.limitMode).safeParse(draft.parameter).success &&
    isSelectedKpiAvailable(draft.parameter, draft.limitMode)
  );
}

export function isWizardStepValid(
  step: WizardStepKey,
  draft: LimitSetupDraft,
  dataLevels: readonly InvestmentComplianceLimitDataLevel[]
) {
  const needsDenominator =
    !filterOnlyLimitModeSchema.safeParse(draft.limitMode).success &&
    draft.parameter.valueType === 'relative';

  switch (step) {
    case 'overview':
      return overviewSchema.safeParse(draft).success;
    case 'parameter':
      return (
        createDataSourceSchema(dataLevels).safeParse(draft.parameter).success &&
        (needsDenominator || isEvaluationValid(draft))
      );
    case 'denominator':
      return (
        !needsDenominator ||
        (draft.parameter.denominator != null &&
          createDataSourceSchema(dataLevels).safeParse(
            draft.parameter.denominator
          ).success &&
          isEvaluationValid(draft))
      );
    case 'workflow':
      return validitySchema.safeParse(draft).success;
    case 'review':
      return true;
  }
}
